import { useEffect } from "react";
import { useAppSelector, useAppDispatch } from "app/hooks";

import { isEmpty } from "lodash";

// Store
import { getAuth, fetchSession } from "store/slice/auth";

// Types
import { ILoggedInUserData, ErrorSession, ISessionResponse } from "store/slice/auth/authSlice.types";

interface IUseAuth {
    hasSession: boolean;
    sessionData: ISessionResponse | null;
    loggedInUserData: ILoggedInUserData;
    isFetching: boolean;
    fetched: boolean;
    person_id: number | undefined;
    error: ErrorSession;
    findOrgRoleByName: (roleName: string) => boolean;
}

const useAuth = (): IUseAuth => {
    const dispatch = useAppDispatch();

    const { data: sessionData, error, isFetching, fetched, redirectUrl } = useAppSelector(getAuth);

    const person_id = sessionData?.authn?.person?.id;
    const userId = sessionData?.authn.user.id;
    const selectedOrg = sessionData?.authn.user.org_selected;

    const hasSession = !isEmpty(sessionData);

    const groups = sessionData?.authz.user.groups || [];
    const userIsolatedGroupId =
        groups.find((group) => group.name === `drax_executive`)?.id ||
        groups.find((group) => group.name === `user_${userId}`)?.id;

    const selectedOrgRoles = sessionData?.authz.user.group_roles.find((group) => group.id === selectedOrg?.group_id);

    const findOrgRoleByName = (roleName: string) => {
        const foundRole = selectedOrgRoles?.roles.find((role) => role.name === roleName);
        return !isEmpty(foundRole);
    };

    const loggedInUserData = {
        userId: sessionData?.authn.user.id || 0,
        name: sessionData?.authn.jwt.given_name || "",
        username: sessionData?.authn.user.username || "",
        lastLoggedInDate: sessionData?.authn.jwt.auth_time || 0,
        groupId: selectedOrg?.group_id || userIsolatedGroupId,
        groups: sessionData?.authz.user.groups || [],
        selectedOrg,
    };

    useEffect(() => {
        if (hasSession) {
            return;
        }

        if (!isFetching && !error) {
            dispatch(fetchSession(redirectUrl));
        }
    }, [hasSession, isFetching, error, redirectUrl, dispatch]);

    return { hasSession, sessionData, loggedInUserData, isFetching, fetched, person_id, error, findOrgRoleByName };
};

export default useAuth;
